import { t } from './i18n';

export const SPEECH_CASE_STATUS = ['Pending', 'Under review', 'Dismissed', 'Removed'];
export const CHAT_RESTRICTION_DURATIONS = [10, 60, 1440];

const numberOrZero = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const firstDefined = (...values) => values.find((value) => value !== undefined && value !== null);

export function normalizeChatRestriction(value, row = {}) {
  const internal = row.restriction && typeof row.restriction === 'object' ? row.restriction : {};
  const source = value && typeof value === 'object' ? value : internal;
  const explicitActive = firstDefined(source.active, row.chat_restriction_active);
  const active = explicitActive == null ? Object.keys(source).length > 0 : Boolean(explicitActive);
  return {
    restrictionId: firstDefined(source.restriction_id, source.restrictionId, source.id, row.chat_restriction_id, internal.restrictionId, internal.restriction_id, internal.id, null),
    active,
    startsAt: firstDefined(source.startsAt, source.starts_at, source.started_at, row.chat_restriction_started_at, null),
    expiresAt: firstDefined(source.expiresAt, source.expires_at, row.chat_restriction_expires_at, null),
    reasonCode: firstDefined(source.reasonCode, source.reason_code, row.chat_restriction_reason_code, null),
    auditId: firstDefined(source.auditId, source.audit_id, row.chat_restriction_audit_id, null),
  };
}

export function normalizeSpeechCase(row = {}) {
  const restriction = normalizeChatRestriction(row.chat_restriction, row);
  return {
    caseId: firstDefined(row.caseId, row.case_id, row.id),
    speechId: firstDefined(row.speechId, row.speech_id, row.speech?.speech_id, row.speech?.id),
    authorId: firstDefined(row.authorId, row.reported_id, row.subject_user_id, row.user?.user_id, row.speech?.user_id),
    message: firstDefined(row.message, row.speech?.message, row.evidence?.message, ''),
    status: firstDefined(row.case_status, row.status, 0),
    contentRevision: firstDefined(row.content_revision, row.contentRevision, row.evidence?.content_revision, null),
    priority: firstDefined(row.priority, null),
    rawReports: numberOrZero(firstDefined(row.rawReports, row.report_count, row.raw_report_count)),
    validReports: numberOrZero(firstDefined(row.validReports, row.valid_report_count, row.qualified_report_count)),
    uniqueReporters: numberOrZero(firstDefined(row.uniqueReporters, row.unique_reporter_count)),
    rollingReporterPool: numberOrZero(firstDefined(row.rollingReporterPool, row.rolling_24h_unique_reporter_count, row.rolling_reporter_pool_count)),
    sameDeviceClusters: numberOrZero(firstDefined(row.sameDeviceClusters, row.same_device_cluster_count, row.device_cluster_count)),
    lateReports: numberOrZero(firstDefined(row.lateReports, row.late_report_count)),
    temporarilyHidden: Boolean(firstDefined(row.temporarilyHidden, row.temporarily_hidden, row.auto_hidden, false)),
    autoHideTriggeredAt: firstDefined(row.autoHideTriggeredAt, row.auto_hide_triggered_at, row.temporarily_hidden_at, null),
    reportExpiresAt: firstDefined(row.report_expires_at, null),
    bubbleExpiresAt: firstDefined(row.bubble_expires_at, row.expired_at, row.speech?.expired_at, null),
    expiresAt: firstDefined(row.expiresAt, row.bubble_expires_at, row.expired_at, row.speech?.expired_at, null),
    restriction,
    reports: Array.isArray(row.reports) ? row.reports : [],
    actions: Array.isArray(row.actions) ? row.actions : [],
    serviceRestriction: row.service_restriction ?? null,
    accountBan: row.account_ban ?? null,
  };
}

export function buildSpeechCaseQueue(items = []) {
  const cases = new Map();
  items.forEach((row) => {
    const normalized = normalizeSpeechCase(row);
    const key = String(normalized.caseId ?? `speech-${normalized.speechId}`);
    const current = cases.get(key);
    if (!current) {
      cases.set(key, { ...normalized, reports: [row] });
      return;
    }
    cases.set(key, {
      ...current,
      ...normalized,
      rawReports: Math.max(current.rawReports, normalized.rawReports),
      validReports: Math.max(current.validReports, normalized.validReports),
      uniqueReporters: Math.max(current.uniqueReporters, normalized.uniqueReporters),
      rollingReporterPool: Math.max(current.rollingReporterPool, normalized.rollingReporterPool),
      sameDeviceClusters: Math.max(current.sameDeviceClusters, normalized.sameDeviceClusters),
      lateReports: Math.max(current.lateReports, normalized.lateReports),
      reports: [...current.reports, row],
    });
  });
  return [...cases.values()];
}

export function speechQueueSummary(data = {}, cases = []) {
  return {
    qualifiedSpeechCount: numberOrZero(firstDefined(data.qualified_speech_count, data.summary?.qualified_speech_count)),
    rollingReporterPool: numberOrZero(firstDefined(data.rolling_24h_unique_reporter_count, data.summary?.rolling_24h_unique_reporter_count)),
    autoHiddenCount: numberOrZero(firstDefined(data.auto_hidden_speech_count, data.summary?.auto_hidden_speech_count, cases.filter((item) => item.temporarilyHidden).length)),
  };
}

export function speechErrorMessage(error) {
  if (!error) return '';
  if (error.status === 401) return t('speechModeration.errors.unauthorized');
  if (error.status === 403) return t('speechModeration.errors.forbidden');
  if (error.status === 404) return t('speechModeration.errors.notFound');
  if (error.status === 409) return t('speechModeration.errors.conflict');
  if (error.status >= 500) return t('speechModeration.errors.server');
  return error.message || 'The request could not be completed.';
}
